import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  EXTERNAL_ID_METADATA_KEY,
  MAX_METADATA_BYTES,
  MetadataRow,
  MetadataRowProblem,
  isLockedRow
} from '@/domain/warehouses'

const {
  Button,
  Input,
  IconButton,
  FieldRoot,
  FieldDescription,
  FieldError,
  FieldsetRoot,
  FieldsetLegend,
  CollapsibleRoot,
  CollapsibleTrigger,
  CollapsiblePanel
} = Base

interface MetadataRowsProps {
  rows: MetadataRow[]
  // Empty until a save is attempted, like the rest of the form's problems.
  problems: Record<string, MetadataRowProblem>
  tooLarge: boolean
  changed: boolean
  editable: boolean
  onChange: (rows: MetadataRow[]) => void
  externalId: string
  // Set when the stored external_id is not plain text, so its row cannot edit it.
  externalIdLocked: boolean
  onExternalIdChange: (externalId: string) => void
}

export const MetadataRows: React.FC<MetadataRowsProps> = ({
  rows,
  problems,
  tooLarge,
  changed,
  editable,
  onChange,
  externalId,
  externalIdLocked,
  onExternalIdChange
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // A save blocked by a row problem opens the section, so the error is never hidden.
  const forcedOpen = Object.keys(problems).length > 0 || tooLarge
  const filled =
    (externalId || externalIdLocked ? 1 : 0) +
    rows.filter(
      (row) => row.loadedKey !== undefined || row.key.trim() || row.value
    ).length

  const update = (id: string, change: Partial<MetadataRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...change } : row)))

  return (
    <CollapsibleRoot
      className="col-span-2"
      open={open || forcedOpen}
      onOpenChange={setOpen}
    >
      <FieldsetRoot>
        <FieldsetLegend>
          <CollapsibleTrigger changed={changed}>
            {t('warehouses.form.metadata')} ·{' '}
            {t('warehouses.form.metadata_count', { count: filled })}
          </CollapsibleTrigger>
        </FieldsetLegend>
        <CollapsiblePanel>
          <FieldRoot>
            <div className="flex items-center gap-2">
              <Input
                aria-label={t('warehouses.form.metadata_key')}
                value={EXTERNAL_ID_METADATA_KEY}
                disabled
              />
              <Input
                aria-label={t('warehouses.form.externalId')}
                placeholder={t('warehouses.form.metadata_value')}
                value={externalId}
                disabled={!editable || externalIdLocked}
                onChange={(event) => onExternalIdChange(event.target.value)}
              />
              {editable ? (
                <IconButton
                  aria-label={t('warehouses.form.metadata_remove', { n: 1 })}
                  disabled
                >
                  ×
                </IconButton>
              ) : null}
            </div>
            <FieldDescription>
              {t(
                externalIdLocked
                  ? 'warehouses.form.externalId_not_text'
                  : 'warehouses.form.externalId_hint'
              )}
            </FieldDescription>
          </FieldRoot>
          {rows.map((row, i) => {
            const locked = isLockedRow(row)
            const problem = problems[row.id]
            return (
              <FieldRoot key={row.id} invalid={!!problem}>
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={t('warehouses.form.metadata_key')}
                    placeholder={t('warehouses.form.metadata_key')}
                    value={row.key}
                    disabled={!editable || locked}
                    onChange={(event) =>
                      update(row.id, { key: event.target.value })
                    }
                  />
                  <Input
                    aria-label={t('warehouses.form.metadata_value')}
                    placeholder={t('warehouses.form.metadata_value')}
                    value={row.value}
                    disabled={!editable || locked}
                    onChange={(event) =>
                      update(row.id, { value: event.target.value })
                    }
                  />
                  {editable ? (
                    <IconButton
                      aria-label={t('warehouses.form.metadata_remove', {
                        n: i + 2
                      })}
                      disabled={locked}
                      onClick={() =>
                        onChange(rows.filter((other) => other.id !== row.id))
                      }
                    >
                      ×
                    </IconButton>
                  ) : null}
                </div>
                {problem ? (
                  <FieldError match>
                    {t(`warehouses.form.problem_metadata_${problem}`)}
                  </FieldError>
                ) : locked ? (
                  <FieldDescription>
                    {t('warehouses.form.metadata_locked')}
                  </FieldDescription>
                ) : null}
              </FieldRoot>
            )
          })}
          {editable ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() =>
                onChange([
                  ...rows,
                  { id: `new-${crypto.randomUUID()}`, key: '', value: '' }
                ])
              }
            >
              {t('warehouses.form.metadata_add')}
            </Button>
          ) : null}
          <FieldRoot invalid={tooLarge}>
            {tooLarge ? (
              <FieldError match>
                {t('warehouses.form.problem_metadata_too_large', {
                  max: MAX_METADATA_BYTES / 1024
                })}
              </FieldError>
            ) : (
              <FieldDescription>
                {t('warehouses.form.metadata_hint')}
              </FieldDescription>
            )}
          </FieldRoot>
        </CollapsiblePanel>
      </FieldsetRoot>
    </CollapsibleRoot>
  )
}
